import React, { Suspense } from 'react';
import { Routes as Switch, Route, Navigate, useLocation } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

import { useUser } from '~/src/utils/user';
import { Spinner } from '/packages/shared-components/Spinner/Spinner';
import { globalError } from '~/src/utils/apollo';
import { isPartnerLegalNameIncomplete, pathIsPublic } from './utils/util';
import { setLoginRedirect } from '/packages/shared-components/utils/cognito';
import TestPlanActionsModalWrapper from './components/api-integration/testplans/modals/TestPlanActions/TestPlansActionsModalWrapper';
import { generateRedirect } from '/packages/shared-components/utils/util';

const ErrorPage = React.lazy(() => import('~src/components/pages/ErrorPage/ErrorPage'));
const FullStoryDisclosure = React.lazy(() => import('./components/pages/FullStoryDisclosure/FullStoryDisclosure'));
const LegacyRouteRedirect = React.lazy(() => import('~/src/utils/LegacyRouteRedirect'));
const TrackerRedirect = React.lazy(() => import('./TrackerRedirect'));

const Home = React.lazy(() => import('~src/components/pages/Home/Home'));
const AdkRoutes = React.lazy(() => import('~src/components/pages/AdkRoutes/AdkRoutes'));
const ApiIntegrationCenter = React.lazy(
  () => import('~src/components/pages/ApiIntegrationCenter/ApiIntegrationCenter'),
);
const LoggedOutPage = React.lazy(() => import('./components/pages/LoggedOut/LoggedOut'));
const LoginErrorPage = React.lazy(() => import('./components/pages/LoginErrorPage/LoginError'));
const ServicesForm = React.lazy(() => import('./components/pages/ServicesForm/ServicesForm'));
const TermsOfUse = React.lazy(() => import('~src/components/pages/TermsOfUsePage/TermsOfUsePage'));
const CompletePartnerProfilePage = React.lazy(
  () => import('~src/components/pages/CompletePartnerProfilePage/CompletePartnerProfilePage'),
);
const SwitchPartner = React.lazy(() => import('~src/components/pages/SwitchPartner/SwitchPartner'));
const APIAdmin = React.lazy(() => import('~src/components/pages/APIAdmin/APIAdmin'));
const ADKAdmin = React.lazy(() => import('~src/components/pages/ADKAdmin/ADKAdmin'));
const AndroidTVAdmin = React.lazy(() => import('~src/components/pages/AndroidTVAdmin/AndroidTVAdmin'));
const CustomerJourneyRoutes = React.lazy(
  () => import('./components/pages/CustomerJourneyRoutes/CustomerJourneyRoutes'),
);
const Profile = React.lazy(() => import('./components/account/profile/Profile'));
const AdminBase = React.lazy(() => import('./components/account/AdminBase'));
const SignUp = React.lazy(() => import('./components/account/SignUp/SignUp'));
const PublicBaseLayout = React.lazy(() => import('./components/account/SignUp/PublicBaseLayout'));
const ChangePassword = React.lazy(() => import('./components/account/SignUp/ChangePassword'));
const SetupMFA = React.lazy(() => import('./components/account/SignUp/SetupMFA'));
const SignIn = React.lazy(() => import('./components/account/SignIn/SignIn'));
const RequestMFA = React.lazy(() => import('./components/account/SignIn/RequestMFA'));
const LoginCallback = React.lazy(() => import('./components/login/LoginCallback'));
const AccessManagementRoutes = React.lazy(
  () => import('./components/account/management/AccessManagementRoutes/AccessManagementRoutes'),
);
const NotificationsRoutes = React.lazy(
  () => import('./components/account/notifications/NotificationsRoutes/NotificationsRoutes'),
);
const ForgotPassword = React.lazy(() => import('./components/account/ForgotPassword/ForgotPassword'));
const RequestAccess = React.lazy(() => import('./components/account/RequestAccess/RequestAccess'));
const PartnerActivationGateway = React.lazy(
  () => import('~src/components/account/PartnerActivation/PartnerActivationGateway'),
);

const PerksIntegrationCenter = React.lazy(
  () => import('./components/pages/PerksIntegrationCenter/PerksIntegrationCenter'),
);

const BuildInfo = React.lazy(() => import('./components/pages/BuildInfo/BuildInfo'));

export const Routes = () => {
  const { currentUser, loadingUser, permissions } = useUser();
  const { pathname, search, hash } = useLocation();
  const globalErrorVar = useReactiveVar(globalError);
  const partnerId = currentUser?.selectedPartner?.id || currentUser?.defaultPartnerId;
  const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);

  const redirect = extractRedirectPath(search);
  if (redirect) setLoginRedirect(redirect);

  if (!pathIsPublic(lastSegment)) {
    if (globalErrorVar === 'unauthorized') {
      return <Navigate to={`/app/sign-in${generateRedirect(pathname, search, hash)}`} />;
    } else if (globalErrorVar) {
      return (
        <Suspense fallback={<Spinner />}>
          <ErrorPage error={globalErrorVar} />
        </Suspense>
      );
    } else if (loadingUser || currentUser === undefined) {
      return <Spinner />;
    } else if (currentUser === null) {
      return (
        <Suspense fallback={<Spinner />}>
          <ErrorPage error="network" fullHeight />
        </Suspense>
      );
    } else if (currentUser?.termsOfService?.accepted !== true && lastSegment !== 'terms-of-use') {
      return <Navigate to="/app/terms-of-use" />;
    } else if (
      currentUser?.termsOfService?.accepted === true &&
      isPartnerLegalNameIncomplete(currentUser) &&
      !['complete-partner-profile', 'terms-of-use'].includes(lastSegment)
    ) {
      return <Navigate to="/app/complete-partner-profile" />;
    } else if (
      currentUser?.fullStoryDisclosureRequired === true &&
      !['terms-of-use', 'full-story-disclosure', 'complete-partner-profile'].includes(lastSegment)
    ) {
      return <Navigate to="/app/full-story-disclosure" />;
    } else if (currentUser?.partnerWorkstreams.length === 0 || !currentUser?.defaultPartnerId) {
      return (
        <Suspense fallback={<Spinner />}>
          <ErrorPage error="forbidden" />
        </Suspense>
      );
    }
  }

  return (
    <Suspense fallback={<Spinner />}>
      <Switch>
        <Route path="/" element={<Navigate to="/app" replace={true} />} />

        {/* Redirect tracker issues to their support ticket */}
        <Route path="/tracker">
          <Route index element={<TrackerRedirect />} />
          <Route path="browse/:issueId" element={<TrackerRedirect />} />
          <Route path="plugins/servlet/desk/category/trac" element={<TrackerRedirect />} />
        </Route>

        <Route path="/app">
          <Route path="gateway/partner-activation" element={<PartnerActivationGateway />} />
          <Route path="gateway/partner-activation/*" element={<PartnerActivationGateway />} />
          <Route path="login/callback" element={<LoginCallback />} />
          <Route path="terms-of-use" element={<TermsOfUse />} />
          <Route path="complete-partner-profile" element={<CompletePartnerProfilePage />} />
          <Route path="full-story-disclosure" element={<FullStoryDisclosure />} />
          <Route path="switch-partner" element={<SwitchPartner />} />
          {permissions.services && <Route path="admin/services" element={<ServicesForm />} />}
          <Route path="admin/api" element={<Navigate to="/app/admin/api/tests" replace={true} />} />
          <Route path="admin/api/:activeTab/*" element={<APIAdmin />} />
          <Route path="admin/adk" element={<Navigate to="/app/admin/adk/suites" replace={true} />} />
          <Route path="admin/adk/:activeTab/*" element={<ADKAdmin />} />
          <Route path="admin/android-tv" element={<AndroidTVAdmin />} />
          <Route path="partner/:partnerId" element={<Home />} />
          <Route path="partner/:partnerId/api" element={<Navigate to="config/provider-config" />} />
          <Route path="partner/:partnerId/api/:activeTab/*" element={<ApiIntegrationCenter />} />
          <Route path="partner/:partnerId/adk/*" element={<AdkRoutes />} />
          <Route path="partner/:partnerId/customer-journey/*" element={<CustomerJourneyRoutes />} />
          <Route path="partner/:partnerId/api/new-test-plan" element={<TestPlanActionsModalWrapper />} />
          <Route path="partner/:partnerId/perks" element={<Navigate to="config/provider-config" />} />
          <Route path="partner/:partnerId/perks/:activeTab/*" element={<PerksIntegrationCenter />} />

          {/* Redirect legacy routes */}
          <Route path={`partner/:partnerId/integration-center`} element={<LegacyRouteRedirect />} />
          <Route
            path={`partner/:partnerId/integration-center/:projectId/:activeTab`}
            element={<LegacyRouteRedirect />}
          />
          <Route path={`partner/:partnerId/test-center`} element={<LegacyRouteRedirect />} />
          <Route path={`partner/:partnerId/project-center`} element={<LegacyRouteRedirect />} />
          <Route path={`partner/:partnerId/project-center/:projectId`} element={<LegacyRouteRedirect />} />
          <Route path={`partner/:partnerId/test-center/:projectId`} element={<LegacyRouteRedirect />} />
          <Route path={`partner/:partnerId/test-center/:projectId/tests/:testId`} element={<LegacyRouteRedirect />} />

          <Route path="loggedOut" element={<LoggedOutPage />} />
          <Route path="loginError" element={<LoginErrorPage />} />

          <Route path="admin" element={<AdminBase partnerHasAccess />}>
            <Route index element={<Navigate to="settings" replace={true} />} />
            <Route path="settings" element={<Profile />} />
          </Route>
          <Route path="admin" element={<AdminBase />}>
            <Route path="access-management/*" element={<AccessManagementRoutes />} />
            <Route path="notifications/*" element={<NotificationsRoutes />} />
          </Route>

          <Route index element={<Navigate to={`partner/${partnerId}`} />} />

          <Route element={<PublicBaseLayout />}>
            <Route path="sign-up" element={<SignUp />} />
            <Route path="sign-in" element={<SignIn />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="setup-mfa" element={<SetupMFA />} />
            <Route path="mfa" element={<RequestMFA />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="request-access" element={<RequestAccess />} />
          </Route>

          <Route path="build-info" element={<BuildInfo />} />

          <Route path="*" element={<ErrorPage error="not_found" />} />
        </Route>
        <Route path="*" element={<ErrorPage error="not_found" />} />
      </Switch>
    </Suspense>
  );
};

function extractRedirectPath(str: string) {
  const params = str.replace(/^\?/, '').split('&');
  const val = params.find((el) => el.includes('redirect='));

  if (val) return decodeURIComponent(val.replace('redirect=', ''));
  return null;
}
